/**
 * ProgressBar molecule utils. Derived state and layout.
 *
 * @internal Used by ProgressBar molecule only.
 */

import type { CSSProperties } from 'react';
import type { ProgressBarLabelPosition } from './ProgressBar.types';
import {
  floatingTagClampMin,
  floatingTagLowPercentThreshold,
} from '../../atoms/ProgressBar/ProgressBar.styles';

export interface ProgressState {
  percent: number;
  indeterminate: boolean;
  displayValue: string | undefined;
  clampedValue: number | undefined;
  ariaLabel: string;
  ariaValuetext: string | undefined;
}

export function getProgressState(
  value: number | undefined,
  max: number,
  indeterminate: boolean,
  label: string | undefined,
  valueLabel: string | undefined,
  ariaLabel: string | undefined
): ProgressState {
  const isIndeterminate = indeterminate || value === undefined;
  const percent =
    isIndeterminate || max <= 0
      ? 0
      : Math.max(0, Math.min(100, Math.round(((value ?? 0) / max) * 100)));
  const displayValue = valueLabel ?? (isIndeterminate ? undefined : `${percent}%`);
  const clampedValue = isIndeterminate ? undefined : Math.max(0, Math.min(max, value ?? 0));
  const resolvedAriaLabel =
    ariaLabel ?? (label ? `${label} progress` : undefined) ?? 'Progress';
  const ariaValuetext = isIndeterminate ? undefined : `${clampedValue} of ${max}`;

  return {
    percent,
    indeterminate: isIndeterminate,
    displayValue,
    clampedValue,
    ariaLabel: resolvedAriaLabel,
    ariaValuetext,
  };
}

export function getFloatingTagPosition(percent: number): CSSProperties {
  const left =
    percent < floatingTagLowPercentThreshold
      ? `calc(100% - ${floatingTagClampMin})`
      : `clamp(${floatingTagClampMin}, ${percent}%, calc(100% - ${floatingTagClampMin}))`;

  return {
    left,
    bottom: '100%',
    marginBottom: '0.25rem',
  };
}

export function isLabelSide(position: ProgressBarLabelPosition): boolean {
  return position === 'side';
}
